import React from 'react'
import { Link } from 'react-router-dom'
import { Bot, Search, HardDrive, ChevronRight } from 'lucide-react'
import { BandHeading } from './OverviewPanel'
import { formatBytes } from './overviewFormat'
import CronStatusChip from './CronStatusChip'

/**
 * AI Spotlight — Agents / Knowledge Search / Personal Library.
 */
export default function AiSpotlight({
  showAgents,
  showKnowledgeSearch,
  showPersonalLibrary,
  agentCount,
  activeAgentCount,
  digestStatus,
  searchableSourceCount,
  personalLibrary,
  loading,
}) {
  if (!showAgents && !showKnowledgeSearch && !showPersonalLibrary) return null

  const quotaPct =
    personalLibrary?.quotaBytes > 0
      ? Math.min(100, Math.round((personalLibrary.bytesUsed / personalLibrary.quotaBytes) * 100))
      : 0

  return (
    <section className="overview-band overview-ai-zone overview-fade-in" aria-label="AI Spotlight">
      <BandHeading title="AI Spotlight" subtitle="Agents, search, and your private library" badge="AI" />
      <div className="overview-spotlight-grid">
        {showAgents ? (
          <article className="overview-spotlight-card">
            <div className="overview-spotlight-icon" aria-hidden>
              <Bot size={22} strokeWidth={1.75} />
            </div>
            <h3 className="overview-spotlight-title">Agents</h3>
            <p className="overview-spotlight-desc">Run the Basecamp digest pipeline stages</p>
            <div className="overview-spotlight-meta">
              {loading && agentCount == null ? (
                <span className="overview-muted-copy">Loading…</span>
              ) : (
                <>
                  <span className="overview-chip">
                    {activeAgentCount ?? 0}/{agentCount ?? 0} active
                  </span>
                  {digestStatus ? <CronStatusChip status={digestStatus} /> : null}
                </>
              )}
            </div>
            <Link to="/agents" className="overview-spotlight-cta">
              Open Agents
              <ChevronRight size={14} aria-hidden />
            </Link>
          </article>
        ) : null}

        {showKnowledgeSearch ? (
          <article className="overview-spotlight-card">
            <div className="overview-spotlight-icon" aria-hidden>
              <Search size={22} strokeWidth={1.75} />
            </div>
            <h3 className="overview-spotlight-title">Knowledge Search</h3>
            <p className="overview-spotlight-desc">Search sources you are allowed to query</p>
            <div className="overview-spotlight-meta">
              <span className="overview-chip overview-chip--stat">
                {searchableSourceCount ?? (loading ? '…' : '—')} sources
              </span>
            </div>
            <Link to="/knowledge" className="overview-spotlight-cta">
              Search now
              <ChevronRight size={14} aria-hidden />
            </Link>
          </article>
        ) : null}

        {showPersonalLibrary && personalLibrary ? (
          <article className="overview-spotlight-card">
            <div className="overview-spotlight-icon" aria-hidden>
              <HardDrive size={22} strokeWidth={1.75} />
            </div>
            <h3 className="overview-spotlight-title">Personal Library</h3>
            <p className="overview-spotlight-desc">Your private documents and storage quota</p>
            <div className="overview-spotlight-meta overview-spotlight-meta--stack">
              <span className="overview-chip overview-chip--stat">
                {personalLibrary.docCount} doc{personalLibrary.docCount === 1 ? '' : 's'}
              </span>
              <div className="overview-quota">
                <div className="overview-quota-track" aria-hidden>
                  <div className="overview-quota-fill" style={{ width: `${quotaPct}%` }} />
                </div>
                <span className="overview-quota-label">
                  {formatBytes(personalLibrary.bytesUsed)} of {formatBytes(personalLibrary.quotaBytes)}
                </span>
              </div>
            </div>
            <Link to="/personal-knowledge" className="overview-spotlight-cta">
              Open library
              <ChevronRight size={14} aria-hidden />
            </Link>
          </article>
        ) : null}
      </div>
    </section>
  )
}
